import { createContext } from '@Utils/Canvas.js'
import * as Utils from '@Utils/Math.js'
import { DOMParser } from '@xmldom/xmldom'
import {
	Canvg,
	presets
} from 'canvg'
import { renderIcons } from '@Utils/Icons.js'
import { getIconUrl } from '@Utils/Icons.js'

const preset = presets.offscreen({
	DOMParser
})
export async function renderSvgWithoutRotation(data, size) {
	const {
		scale = 1,
		svg,
		rotation,
		maskColor
	} = data
	if (!size) {
		size = [24, 24]
	}
	const [canvas, ctx] = createContext(100, 100, 'IconUtuils.renderSvg')
	const v = await Canvg.from(ctx, svg, preset)
	let width = size[0] * scale
	let height = size[1] * scale
	v.resize(width, height, 'xMidYMid meet')
	await v.render()
	return canvas
}
export async function renderSvg(data, size) {
	const {
		scale = 1,
		svg,
		rotation,
		maskColor
	} = data
	if (!size) {
		size = [24, 24]
	}
	const [canvas, ctx] = createContext(100, 100, 'IconUtuils.renderSvg')
	const v = await Canvg.from(ctx, svg, preset)
	let width = size[0] * scale
	let height = size[1] * scale
	v.resize(width, height, 'xMidYMid meet')
	await v.render()
	if (width < 1) {
		width = 1
	}
	if (height < 1) {
		height = 1
	}
	let diagonal = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2))
	let offsetX = (diagonal - width) / 2
	let offsetY = (diagonal - height) / 2
	let centerX = diagonal / 2
	let centerY = diagonal / 2
	const [rotationCanvas, rotationContext] = createContext(diagonal, diagonal, 'IconUtuils.renderSvg.rotationCanvas')
	if (maskColor) {
		rotationContext.fillStyle = maskColor
		rotationContext.fillRect(0, 0, diagonal, diagonal)
		rotationContext.globalCompositeOperation = 'destination-in'
	}
	if (rotation && !data.directional) {
		rotationContext.translate(centerX, centerY)
		rotationContext.rotate(rotation)
		rotationContext.drawImage(canvas, -width / 2, -width / 2, width, height)
	} else {
		rotationContext.drawImage(canvas, offsetX, offsetY, width, height)
	}
	return rotationCanvas
}
export async function createIconGridAsync(props) {
	const scale = props.iconScale
	const split = props.iconGridSplit
	const direction = props.iconGridDirection
	const icons = props.icons
	const iconSize = scale * props.iconSize
	const iconSpacing = scale * props.iconSpacing
	const iconCollectionPadding = scale * props.iconCollectionPadding
	const iconCollectionPlacement = props.iconCollectionPlacement
	const cr = scale * 6
	/// calculate the size of the grid
	let width = 0
	let height = 0
	if (direction === 'horizontal') {
		let columns = icons.length < split ? icons.length : split
		let rows = Math.ceil(icons.length / split)
		height = columns * iconSize + (columns - 1) * iconSpacing + iconCollectionPadding * 2
		width = rows * iconSize + (rows - 1) * iconSpacing + iconCollectionPadding * 2
	} else {
		let rows = Math.ceil(icons.length / split)
		let columns = icons.length < split ? icons.length : split
		width = columns * iconSize + (columns - 1) * iconSpacing + iconCollectionPadding * 2
		height = rows * iconSize + (rows - 1) * iconSpacing + iconCollectionPadding * 2
	}
	let cornerRadius = [cr, cr, cr, cr]
	if (iconCollectionPlacement === "topleft") {
		cornerRadius = [0, cr, cr, cr]
	} else if (iconCollectionPlacement === "topright") {
		cornerRadius = [cr, 0, cr, cr]
	} else if (iconCollectionPlacement === "bottomleft") {
		cornerRadius = [cr, cr, cr, 0]
	} else if (iconCollectionPlacement === "bottomright") {
		cornerRadius = [cr, cr, 0, cr]
	}
	const [canvas, context] = createContext(width, height, 'IconUtils.createIconGrid')
	context.beginPath()
	context.roundRect(0, 0, width, height, cornerRadius)
	context.fillStyle = 'white'
	context.fill()
	for (let i = 0; i < icons.length; i++) {
		let icon = icons[i]
		if (direction === 'horizontal') {
			let row = Math.floor(i / split)
			let column = i % split
			let ix = row * (iconSize + iconSpacing) + iconCollectionPadding
			let iy = column * (iconSize + iconSpacing) + iconCollectionPadding
			let svg = await renderSvgWithoutRotation({
				svg: renderIcons[icon]
			}, [iconSize, iconSize])
			context.drawImage(svg, ix, iy, svg.width, svg.height)
		} else {
			let row = Math.floor(i / split)
			let column = i % split
			let ix = column * (iconSize + iconSpacing) + iconCollectionPadding
			let iy = row * (iconSize + iconSpacing) + iconCollectionPadding
			let svg = await renderSvgWithoutRotation({
				svg: renderIcons[icon]
			}, [iconSize, iconSize])
			context.drawImage(svg, ix, iy, svg.width, svg.height)
		}
	}
	return canvas
}
export function createIconGrid(feature, el, scale, imageCache) {
	const split = feature.get('iconGridSplit') ?? el.defaultValues.iconGridSplit ?? 0
	const direction = feature.get('iconGridDirection') ?? el.defaultValues.iconGridDirection ?? 'horizontal'
	const icons = feature.get('icons') ?? el.defaultValues.icons ?? []
	const iconSize = scale * 24
	const iconSpacing = scale * (feature.get('iconSpacing') ?? el.defaultValues.iconSpacing ?? 5)
	const iconCollectionPadding = scale * (feature.get('iconCollectionPadding') ?? el.defaultValues.iconCollectionPadding ?? 5)
	const iconCollectionPlacement = feature.get('iconCollectionPlacement') ?? el.defaultValues.iconCollectionPlacement ?? "center"
	const cr = scale * 6
	/// calculate the size of the grid
	let width = 0
	let height = 0
	let color = 'white'
	if (icons.length === 0) {
		width = 24
		height = 24
		color = 'red'
	} else {
		if (direction === 'horizontal') {
			let columns = icons.length < split ? icons.length : split
			let rows = Math.ceil(icons.length / split)
			height = columns * iconSize + (columns - 1) * iconSpacing + iconCollectionPadding * 2
			width = rows * iconSize + (rows - 1) * iconSpacing + iconCollectionPadding * 2
		} else {
			let rows = Math.ceil(icons.length / split)
			let columns = icons.length < split ? icons.length : split
			width = columns * iconSize + (columns - 1) * iconSpacing + iconCollectionPadding * 2
			height = rows * iconSize + (rows - 1) * iconSpacing + iconCollectionPadding * 2
		}
	}
	let cornerRadius = [cr, cr, cr, cr]
	if (iconCollectionPlacement === "topleft") {
		cornerRadius = [0, cr, cr, cr]
	} else if (iconCollectionPlacement === "topright") {
		cornerRadius = [cr, 0, cr, cr]
	} else if (iconCollectionPlacement === "bottomleft") {
		cornerRadius = [cr, cr, cr, 0]
	} else if (iconCollectionPlacement === "bottomright") {
		cornerRadius = [cr, cr, 0, cr]
	}
	const [canvas, context] = createContext(width, height, 'IconUtils.createIconGrid')
	context.beginPath()
	context.roundRect(0, 0, width, height, cornerRadius)
	context.fillStyle = color
	context.fill()
	for (let i = 0; i < icons.length; i++) {
		let icon = icons[i]
		if (direction === 'horizontal') {
			let row = Math.floor(i / split)
			let column = i % split
			let ix = row * (iconSize + iconSpacing) + iconCollectionPadding
			let iy = column * (iconSize + iconSpacing) + iconCollectionPadding
			if (imageCache[icon]) {
				context.drawImage(imageCache[icon], ix, iy, iconSize, iconSize)
			}
		} else {
			let row = Math.floor(i / split)
			let column = i % split
			let ix = column * (iconSize + iconSpacing) + iconCollectionPadding
			let iy = row * (iconSize + iconSpacing) + iconCollectionPadding
			if (imageCache[icon]) {
				context.drawImage(imageCache[icon], ix, iy, iconSize, iconSize)
			}
		}
	}
	return canvas
}